import { IndustryReadDTO } from "api/catalog/__generated__/CatalogueAPI";
import { CatalogueApiVersion } from "api/catalog/catalogApi";
import { useIndustriesAll } from "api/catalog/queries/useIndustries";
import { SelectGroupTitle, SelectOption } from "appkit-react";
import Select from "components/Select";
import React, { useCallback, useMemo } from "react";

interface Props {
  value: string[];
  onValueChange: (value: string[]) => void;
  placeholder?: string;
  hasError?: boolean;
  errMsg?: string | boolean | null;
  tenant: string;
  apiVersion?: CatalogueApiVersion;
}
export default function ServiceIndustryTagSelect({ value, onValueChange, apiVersion, ...props }: Props) {
  const { data: industriesAll = {} } = useIndustriesAll(undefined, apiVersion);

  const industries: IndustryReadDTO[] = useMemo(
    () => industriesAll[props.tenant] ?? industriesAll[`HTTP://${props.tenant}/`] ?? [],
    [industriesAll, props.tenant]
  );
  const withSubIndustries = useMemo(
    () => industries.filter((industry) => industry.subIndustries?.length),
    [industries]
  );
  const others = useMemo(() => industries.filter((industry) => !industry.subIndustries?.length), [industries]);

  const existingValues = useMemo(
    () =>
      value?.filter(
        (v) =>
          others.some((o) => o.id === v) || withSubIndustries.some((i) => i.subIndustries?.some((si) => si.id === v))
      ),
    [others, value, withSubIndustries]
  );

  const handleOnChange = useCallback(
    (newValue: string[]) => {
      onValueChange?.([...(value || []).filter((v) => !existingValues.includes(v)), ...newValue]);
    },
    [existingValues, onValueChange, value]
  );

  return (
    <Select clearable value={existingValues} onSelect={handleOnChange} placeholder="Industry tags" multiple {...props}>
      {withSubIndustries.flatMap((industry) => [
        <SelectGroupTitle key={industry.id}>{industry.name}</SelectGroupTitle>,
        ...industry.subIndustries!.map((subIndustry) => (
          <SelectOption key={subIndustry.id} value={subIndustry.id}>
            {subIndustry.name}
          </SelectOption>
        )),
      ])}
      {others?.length > 0 && (
        <>
          <SelectGroupTitle>Others</SelectGroupTitle>
          {others.map((industry) => (
            <SelectOption key={industry.id} value={industry.id}>
              {industry.name}
            </SelectOption>
          ))}
        </>
      )}
    </Select>
  );
}
